import React, { useEffect, useState } from 'react';
import { onboardingAPI } from '../../services/api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { IdentificationCard, Plus, PencilSimpleLine, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';

const EMPTY = { name: '', description: '', required: true };

const RequiredDocumentsSection = ({ onChanged }) => {
  const [docTypes, setDocTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const res = await onboardingAPI.listDocTypes(); setDocTypes(res.data); }
    catch { toast.error('Failed to load required documents'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openDialog = (d = null) => { setEditing(d); setForm(d ? { name: d.name, description: d.description || '', required: d.required !== false } : EMPTY); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Enter a document name'); return; }
    setSaving(true);
    try {
      if (editing) await onboardingAPI.updateDocType(editing.id, form);
      else await onboardingAPI.createDocType(form);
      toast.success(editing ? 'Document updated' : 'Document added');
      setOpen(false); await load(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const toggleRequired = async (d, v) => {
    try { await onboardingAPI.updateDocType(d.id, { required: v }); setDocTypes(prev => prev.map(x => (x.id === d.id ? { ...x, required: v } : x))); onChanged?.(); }
    catch { toast.error('Failed to update'); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Remove "${d.name}" from the packet?`)) return;
    try { await onboardingAPI.deleteDocType(d.id); setDocTypes(prev => prev.filter(x => x.id !== d.id)); toast.success('Removed'); onChanged?.(); }
    catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="space-y-4" data-testid="required-documents-section">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h3 className="font-heading text-lg font-semibold flex items-center gap-2"><IdentificationCard weight="duotone" className="w-5 h-5 text-primary" /> Required Documents</h3>
          <p className="text-sm text-muted-foreground mt-0.5">Applicants photograph or upload these from their phone. Required ones must be uploaded before the applicant shows as complete.</p>
        </div>
        <Button variant="outline" className="rounded-xl gap-2 shrink-0" onClick={() => openDialog()} data-testid="add-doc-type-btn"><Plus weight="bold" className="w-4 h-4" /> Add Document</Button>
      </div>

      {loading ? <div className="py-6 text-center text-sm text-muted-foreground">Loading…</div> : docTypes.length === 0 ? (
        <Card className="rounded-2xl border-dashed"><CardContent className="py-8 text-center text-sm text-muted-foreground">No documents requested yet — e.g. Driver's License, CPR Card, TB Test results.</CardContent></Card>
      ) : (
        <Card className="rounded-2xl overflow-hidden"><div className="divide-y divide-border">
          {docTypes.map(d => (
            <div key={d.id} className="flex items-center gap-4 px-4 py-3" data-testid={`doc-type-${d.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{d.name} {d.required ? <Badge className="rounded-full text-[10px] ml-1 bg-rose-100 text-rose-800">Required</Badge> : <Badge variant="secondary" className="rounded-full text-[10px] ml-1">Optional</Badge>}</div>
                {d.description && <p className="text-xs text-muted-foreground truncate">{d.description}</p>}
              </div>
              <label className="flex items-center gap-2 text-xs"><Switch checked={d.required !== false} onCheckedChange={(v) => toggleRequired(d, v)} data-testid={`doc-type-required-${d.id}`} /> Required</label>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openDialog(d)} data-testid={`edit-doc-type-${d.id}`}><PencilSimpleLine className="w-4 h-4" /></Button>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive" onClick={() => remove(d)} data-testid={`delete-doc-type-${d.id}`}><Trash className="w-4 h-4" /></Button>
            </div>
          ))}
        </div></Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="doc-type-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? 'Edit Document' : 'Add Required Document'}</DialogTitle>
            <DialogDescription>Applicants will see this in their onboarding checklist.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2">
            <div className="space-y-1.5"><Label>Document name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Driver's License" className="rounded-xl" data-testid="doc-type-name-input" /></div>
            <div className="space-y-1.5"><Label>Instructions</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Front side, must not be expired" className="rounded-xl" data-testid="doc-type-desc-input" /></div>
            <label className="flex items-center gap-2 text-sm"><Switch checked={form.required} onCheckedChange={(v) => setForm({ ...form, required: v })} data-testid="doc-type-required-switch" /> Required to complete onboarding</label>
            <div className="flex gap-2 pt-1">
              <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" className="flex-1 rounded-xl" disabled={saving} data-testid="doc-type-save-btn">{saving ? 'Saving…' : editing ? 'Update' : 'Add'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RequiredDocumentsSection;
